/**
 * A startup's priority band (PRD §5.2).
 *
 * A ≥3.8, B 2.5–3.8, C below. The letter is the content, not decoration: the
 * three bands differ only in hue otherwise, which PRD §9.7 does not permit as
 * the sole signal. An unscored startup renders a muted dash rather than
 * nothing, so a missing score is visibly missing rather than ambiguous.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.16, PRD §5.2, §9.7
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

import { BAND_STYLE, type Band } from './bands';

export interface BandBadgeProps {
  /** Null when the startup has not been scored. */
  band: Band | null;
  className?: string;
}

export function BandBadge({ band, className }: BandBadgeProps): React.JSX.Element {
  const { t } = useTranslation();

  const label = band === null ? t('band.none') : t(`band.${band}`);

  return (
    <span
      className={cn(
        'font-display inline-grid size-5 flex-none place-items-center rounded-full',
        'text-[11px] font-extrabold',
        band === null ? 'bg-sem-mute-bg text-sem-mute' : BAND_STYLE[band],
        className,
      )}
      /* The glyph alone ("A") does not say what it means, so the full label is
         exposed to assistive technology. */
      role="img"
      aria-label={label}
    >
      <span aria-hidden="true">{band ?? '–'}</span>
    </span>
  );
}
